import React, { FC, useEffect, useState } from "react";
import moment from "moment-timezone";
import { getDaysRemaining } from "helpers/common/roleHelper";
import SubscriptionCardButton from "./SubscriptionCardButton";
import { addOneDay, calculateDateDifference } from "helpers/utils";

type PropsType = {
  currentPlan: any;
};

/** Animates a number from 0 → target. Returns rounded current value. */
const useCountUp = (target: number, duration = 900): number => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(target)) {
      setValue(0);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setValue(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return Math.round(value);
};

const RADIUS = 70;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS; // ≈ 439.82

const SubscriptedPlanBannerCard: FC<PropsType> = ({ currentPlan }) => {
  const daysRemainingRaw = getDaysRemaining(currentPlan?.endDate);
  const isExpired = daysRemainingRaw < 0;
  const daysRemaining = Math.max(0, daysRemainingRaw);
  const totalDays = Math.max(
    1,
    calculateDateDifference(currentPlan?.startDate, currentPlan?.endDate) || 1
  );
  const isTrial = totalDays <= 15;
  const elapsedDays = Math.max(0, totalDays - daysRemaining);
  const elapsedPct = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));
  const remainingPct = 100 - elapsedPct;
  const ringOffset = CIRCUMFERENCE * (1 - remainingPct / 100);

  const animatedDays = useCountUp(daysRemaining);

  const productGuid =
    currentPlan?.subscriptionPricingPlan?.subscriptionPlanProduct?.productType
      ?.guid;
  const planName =
    currentPlan?.subscriptionPricingPlan?.subscriptionPlanProduct
      ?.subscriptionPlan?.subscriptionPlanDisplayName;
  const frequency =
    currentPlan?.subscriptionPricingPlan?.billingCycle?.billingCycleDisplayName;
  const currencySymbol = currentPlan?.currency?.currencySymbol;
  const totalAmount = currentPlan?.totalAmount;

  /* -------------------- EXPIRED STATE -------------------- */
  if (isExpired) {
    return (
      <div className="ni-dash-cockpit ni-dash-cockpit--expired">
        <div
          className="ni-dash-cockpit-body"
          style={{ justifyContent: "space-between", alignItems: "flex-start" }}
        >
          <div className="ni-dash-expired-header">
            <div className="ni-dash-expired-title">
              Your subscription has
              <span className="danger">expired</span>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" fill="var(--ni-dash-danger-tint)" />
                <path d="M12 8v4M12 16h.01" stroke="var(--ni-dash-danger)" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
            </div>
            <div className="ni-dash-expired-sub">
              Renew now to continue accessing all features and avoid losing
              your data.
            </div>
          </div>
          <div className="ni-dash-cockpit-actions">
            <SubscriptionCardButton havePlan={true} productGuid={productGuid} />
          </div>
        </div>
      </div>
    );
  }

  /* -------------------- ACTIVE STATE -------------------- */
  const expiringSoon = daysRemaining <= 7;
  const renewSoon = daysRemaining <= 15 && daysRemaining > 0;

  return (
    <div className="ni-dash-cockpit">
      {/* SVG gradient defs */}
      <svg className="ni-dash-svg-defs" aria-hidden="true">
        <defs>
          <linearGradient id="niDashRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="var(--ni-dash-brand)" />
            <stop offset="100%" stopColor="var(--ni-dash-brand-3)" />
          </linearGradient>
        </defs>
      </svg>

      <div className="ni-dash-cockpit-header">
        <div className="ni-dash-cockpit-title-wrap">
          <div className="ni-dash-cockpit-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </div>
          <div>
            <div className="ni-dash-cockpit-title">
              {isTrial ? "Free trial" : "Subscription"}
            </div>
            <div className="ni-dash-cockpit-subtitle">
              {planName || "Active plan"}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!isTrial && frequency && (
            <span className="ni-dash-plan-badge">
              {planName} · {frequency}
            </span>
          )}
          {isTrial && (
            <span className="ni-dash-plan-badge ni-dash-plan-badge--trial">
              Trial active
            </span>
          )}
          {renewSoon && (
            <span
              className={`ni-dash-plan-badge ${
                expiringSoon ? "ni-dash-plan-badge--urgent" : "ni-dash-plan-badge--trial"
              }`}
            >
              {isTrial ? "Trial ends soon" : "Renew soon"}
            </span>
          )}
        </div>
      </div>

      <div className="ni-dash-cockpit-body">
        {/* Ring chart */}
        <div className="ni-dash-ring-wrap">
          <svg className="ni-dash-ring-chart" width="160" height="160" aria-hidden="true">
            <circle className="ni-dash-ring-bg" cx="80" cy="80" r={RADIUS} />
            <circle
              className="ni-dash-ring-fg"
              cx="80"
              cy="80"
              r={RADIUS}
              style={
                {
                  "--ni-dash-ring-offset": ringOffset,
                } as React.CSSProperties
              }
            />
          </svg>
          <div className="ni-dash-ring-content">
            <div className="ni-dash-ring-number">{animatedDays}</div>
            <div className="ni-dash-ring-label">
              {daysRemaining === 1 ? "day left" : "days left"}
            </div>
          </div>
        </div>

        {/* Stats column */}
        <div className="ni-dash-cockpit-stats">
          {!isTrial && totalAmount !== undefined && (
            <div className="ni-dash-stat-row">
              <div className="ni-dash-stat-row-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
                Plan charges
              </div>
              <div className="ni-dash-stat-row-value ni-dash-stat-row-value--amount ni-dash-stat-row-value--currency">
                <span className="currency-fontFamily">{currencySymbol}</span>
                {totalAmount}
              </div>
            </div>
          )}

          {isTrial && (
            <div className="ni-dash-stat-row">
              <div className="ni-dash-stat-row-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                </svg>
                Trial started
              </div>
              <div className="ni-dash-stat-row-value">
                {moment(currentPlan?.startDate).format("Do MMM YYYY")}
              </div>
            </div>
          )}

          <div className="ni-dash-stat-row">
            <div className="ni-dash-stat-row-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {isTrial ? "Trial ends" : "Next billing"}
            </div>
            <div className="ni-dash-stat-row-value">
              {isTrial
                ? moment(currentPlan?.endDate?.split("T")[0]).format("Do MMM YYYY")
                : moment(
                    addOneDay(currentPlan?.endDate?.split("T")[0])
                  ).format("Do MMM YYYY")}
            </div>
          </div>

          <div className="ni-dash-cycle-bar-wrap">
            <div className="ni-dash-cycle-meta">
              <span>Billing cycle</span>
              <span>
                <strong>{Math.round(elapsedPct)}%</strong> elapsed
              </span>
            </div>
            <div className="ni-dash-cycle-bar">
              <div
                className="ni-dash-cycle-fill"
                style={
                  {
                    "--ni-dash-bar-fill": `${elapsedPct}%`,
                  } as React.CSSProperties
                }
              />
            </div>
          </div>

          <div className="ni-dash-cockpit-actions">
            <SubscriptionCardButton havePlan={true} productGuid={productGuid} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptedPlanBannerCard;
